import type { GeneralObject } from '~/types/CommonTypes';
import {
  SalesCommission,
  type SalesCommissionsTotalizer,
} from '~models/SalesCommissions/SalesCommission';
import { SalesCommissionListItem } from '~models/SalesCommissions/SalesCommissionListItem';
import { convertToBrCurrency } from '~/utils/textUtils';
import { listCommissions, listCommissionsTotalizers, showCommission } from '~/services/apiCompany';
import { useAuthStore } from '~/stores/useAuthStore';
import { useTimer } from '~/composables/useTimer';

import { isEmptyValue } from '@scansource-brazil/zeus-core-ui/data';
import { reactive, ref, computed, inject } from 'vue';
import { useProfileStore } from '~stores/useProfileStore';
import FeatureFlagService from '~/services/FeatureFlagService';

export const useCommissionsStore = defineStore(
  'MY_SCAN_SOURCE_COMMISSIONS_STORE',
  () => {
    const AUTH_STORE = useAuthStore();
    const API = { listCommissions, showCommission };
    const PROFILE_STORE = useProfileStore();

    const { profile } = storeToRefs(PROFILE_STORE);

    const totalizerTimer = useTimer();
    const listTimer = useTimer();

    const { pagination, CallSetPagination, CallResetPagination, CallPaginateAndFetch } =
      usePaginator();

    const commission = reactive(new SalesCommission());
    const commissionLoading = ref<boolean>(true);
    const commissionList = ref<SalesCommissionListItem[]>([]);
    const commissionListLoading = ref<boolean>(true);
    const commissionTotalizers = ref<SalesCommissionsTotalizer[]>([]);
    const commissionTotalizersLoading = ref<boolean>(true);
    const commissionListFilters = ref<SalesCommissionFilters>(new SalesCommissionFilters());

    const getCommission = computed(() => commission);
    const getCommissionList = computed(() => commissionList.value);
    const getCommissionListFilters = computed(() => commissionListFilters.value);
    const getCommissionListLoading = computed(() => commissionListLoading.value);
    const getCommissionTotalizersLoading = computed(() => commissionTotalizersLoading.value);
    const getCommissionLoading = computed(() => commissionLoading.value);
    const getCommissionTotalizers = computed(() => commissionTotalizers.value);
    const isActiveFeature = computed(() => {
      if (FeatureFlagService !== null) {
        const { currentEconomicGroupMember } = AUTH_STORE.session;
        const whiteList = FeatureFlagService.findByFlagName('WhiteListCommissions');

        return (
          FeatureFlagService?.isOn('CommissionsFeaturesAvailable') ||
          (Array.isArray(whiteList?.defaultValue) && whiteList.defaultValue.includes(currentEconomicGroupMember))
        );
      }

      return false;
    });

    function updateCommissionListDates(dates: Date[]) {
      commissionListFilters.value.date = dates;
      // dispatchGetCommissions(true);
    }
    function setCommissionList(content: SalesCommissionListItem[] = []) {
      commissionList.value = content;
    }
    function clearCommissionList() {
      commissionList.value = [];
    }
    function setCommissionTotalizers(totalizers: null | GeneralObject) {
      commissionTotalizers.value =
        totalizers === null
          ? []
          : [
              {
                name: 'commissionAmount',
                title: 'COMMISSIONS.TOTALIZERS.TOTAL',
                amount: totalizers.commissionAmount,

                color: 'text-primary',
                borderColor: 'border-primary',
                icon: ICONS.COMMISSIONS,
                tip: 'COMMISSIONS.TOTALIZER_CARD_TOTALS.TIP',
              },
              {
                name: 'totalAmountReceivable',
                title: 'COMMISSIONS.TOTALIZERS.RECEIVABLE',
                amount: totalizers.totalAmountReceivable,
                color: ' text-warning',
                borderColor: 'border-warning',
                icon: ICONS.PAYMENTS,
                tip: 'COMMISSIONS.TOTALIZER_CARD_PREVIEW.TIP',
              },
              {
                name: 'totalAvailableRedemption',
                title: 'COMMISSIONS.TOTALIZERS.AVAILABLE_REDEMPTION',
                amount: totalizers.totalAvailableRedemption,
                color: ' text-success',
                borderColor: 'border-success',
                icon: ICONS.CREDIT_CARD,
                tip: 'COMMISSIONS.TOTALIZER_CARD_CREDITED.TIP',
              },
            ];
    }
    function clearCommissionTotalizers() {
      commissionTotalizers.value = [];
    }

    function clearCommissionListFilters() {
      commissionListFilters.value.resetFilters();
      dispatchGetCommissions(true);
      CallResetPagination();
    }

    /**
     * Formats the numeric values in the totalizers object using Brazilian Portuguese number formatting.
     *
     * @param {GeneralObject} totalizers - An object containing commission-related numeric values
     * @returns {object} An object with the same structure as the input but with values formatted as strings
     * with two decimal places using Brazilian Portuguese number format (e.g., "1.234,56")
     * @property {string} commissionAmount - Formatted commission amount
     * @property {string} totalAmountReceivable - Formatted total amount receivable
     * @property {string} totalAvailableRedemption - Formatted total available for redemption
     */
    function adaptTotalizersValues(totalizers: GeneralObject) {
      return {
        commissionAmount: convertToBrCurrency(totalizers.totalValueCommissions),
        totalAmountReceivable: convertToBrCurrency(totalizers.totalValuePreviewCommissions),
        totalAvailableRedemption: convertToBrCurrency(totalizers.totalValueCreditedCommissions),
      };
    }

    function $reset() {
      commission.clearData();
      clearCommissionList();
      clearCommissionListFilters();
      clearCommissionTotalizers();
    }

    async function dispatchShowCommission(salesCommissionId: string): Promise<void> {
      if (!isActiveFeature.value) return;
      commissionLoading.value = true;
      commission.clearData();

      await API.showCommission(AUTH_STORE.session.currentEconomicGroupMember, salesCommissionId)
        .then(
          ({ data, success }) => {
            if (isUndefined(data) && isUndefined(success)) return commission.clearData();
            if (success === false) return commission.clearData();
            if (success === true && (isNullOrUndefined(data) || isEmptyValue(data)))
              return commission.clearData();
            return commission.setDataFromDto(data);
          },
          (error) => {
            throw error;
          },
        )
        .finally(() => {
          commissionLoading.value = false;
        });
    }

    async function dispatchGetCommissions(force = false): Promise<void> {
      if (!isActiveFeature.value) return;
      if (profile.value.isCommercial) return;
      if (!listTimer.isExpired.value && !force) return;

      commissionListLoading.value = true;
      await API.listCommissions(AUTH_STORE.session.currentEconomicGroupMember, {
        ...SalesCommissionFilters.adapter(commissionListFilters.value),
        ...pagination.value,
      }).then(
        ({ items, pagination, success }) => {
          if (success && Array.isArray(items) && items.length > 0) {
            setCommissionList(items.map(SalesCommissionListItem.adapter));
            CallSetPagination(pagination);
            listTimer.startTimer();
            commissionListLoading.value = false;
          } else {
            setCommissionList();
            CallResetPagination();
            commissionListLoading.value = false;
          }
        },
        () => {
          setCommissionList();
          CallResetPagination();
          commissionListLoading.value = false;
        },
      );
    }

    async function dispatchGetCommissionTotalizers(force = false): Promise<void> {
      if (!isActiveFeature.value) return;
      if (profile.value.isCommercial) return;
      if (!totalizerTimer.isExpired.value && !force) return;

      commissionTotalizersLoading.value = true;
      await listCommissionsTotalizers(AUTH_STORE.session.currentEconomicGroupMember)
        .then((data: Record<string, any>) => {
          if (!data) return;
          const { success, data: content } = data;
          if (success && content) {
            setCommissionTotalizers(adaptTotalizersValues(content));
            totalizerTimer.startTimer();
            return;
          } else {
            setCommissionTotalizers(null);
            totalizerTimer.resetTimer();
          }
        })
        .finally(() => {
          commissionTotalizersLoading.value = false;
        });
    }

    async function onChangeEconomicGroup(): Promise<void> {
      Promise.all([dispatchGetCommissions(true), dispatchGetCommissionTotalizers(true)]);
    }

    async function onSessionOpen(): Promise<void> {
      Promise.all([dispatchGetCommissions(), dispatchGetCommissionTotalizers()]);
    }

    return {
      commission: getCommission,
      commissionList: getCommissionList,
      commissionListLoading: getCommissionListLoading,
      commissionLoading: getCommissionLoading,
      commissionTotalizersLoading: getCommissionTotalizersLoading,
      commissionListFilters: getCommissionListFilters,
      commissionTotalizers: getCommissionTotalizers,
      commissionListPagination: pagination,
      isActiveFeature,
      CallCommissionListPaginate: async (data: Record<string, any>) =>
        await CallPaginateAndFetch(data, async () => await dispatchGetCommissions(true)),
      clearCommissionListFilters,
      dispatchFilterCommissions: () => {
        CallResetPagination();
        return dispatchGetCommissions(true);
      },
      dispatchGetCommissionTotalizers,
      dispatchShowCommission,
      onChangeEconomicGroup,
      onSessionClose: $reset,
      onSessionOpen,
      updateCommissionListDates,
    };
  },
  { persist: true },
);
